"use client";

import Section from "@/components/static/Section";
import Headline from "@/components/static/Headline";
import Text from "@/components/static/Text";
import parse from "html-react-parser";
import Image from "next/image";
import Buttons from "@/components/static/Buttons";
import Button from "@/components/static/Button";
import CountUp from "react-countup";
import { SectionProps, HeadlineProps, ImageProps, ButtonProps } from "@/lib/types";
import { cn } from "@/lib/utils";

type Content = {
    headline: HeadlineProps;
    text: string;
    settings: SectionProps;
    buttons: ButtonProps[];
    inner: [
        {
            image: ImageProps;
            stat: {
                pre: string;
                value: number;
                post: string;
            };
            headline: string;
            text: string;
        }
    ];
};

export default function Stats_31({ content }: { content: Content }) {
    return (
        <Section dataComponent='Stats_31' settings={content.settings}>
            <div className='container'>
                <div className='mb-12 w-full max-w-lg md:mb-18 lg:mb-20'>
                    <Headline
                        tag={content.headline.tag}
                        topline={content.headline.topline}
                        className='font-medium'
                    >
                        {content.headline.title}
                    </Headline>
                    {content.text && <Text className='text-md'>{parse(content.text)}</Text>}
                    {content.buttons && (
                        <div className='mt-6 flex items-start justify-start gap-x-4 md:mt-8'>
                            <Buttons className='buttons gap-x-8'>
                                {content.buttons?.map((button, index) => {
                                    return (
                                        <Button
                                            as='link'
                                            href={button?.link?.url}
                                            type={button?.type}
                                            key={index}
                                        >
                                            {button?.link?.title}
                                        </Button>
                                    );
                                })}
                            </Buttons>
                        </div>
                    )}
                </div>

                <div className='grid grid-cols-1 auto-rows-max rounded-[32px] overflow-hidden md:grid-cols-3'>
                    {content?.inner &&
                        content?.inner?.map((child, index: number) => {
                            if (index === 0) {
                                return (
                                    <div
                                        key={index}
                                        className='flex relative flex-col w-full min-h-[670px]'
                                    >
                                        <div className='absolute top-0 left-0 h-full'>
                                            <Image
                                                src={child.image.url}
                                                height={child.image.height}
                                                width={child.image.width}
                                                alt={child.image.alt}
                                                className='object-cover h-full'
                                            />
                                        </div>
                                        <div className='absolute size-full bg-brand-dark bg-opacity-40'></div>
                                        <div className='flex flex-col justify-between z-10 p-8 h-full'>
                                            <div className='text-h5 text-white leading-[120%]'>
                                                {child.headline}
                                            </div>
                                            <div>
                                                <div>
                                                    <div className='flex text-h0 xl:text-[70px] xxl:text-[90px] font-medium text-white gap-2 leading-[120%]'>
                                                        <CountUp
                                                            end={child.stat.value}
                                                            prefix={child.stat?.pre}
                                                            suffix={child.stat.post}
                                                            separator=''
                                                            decimal=','
                                                            decimals={
                                                                child.stat.value
                                                                    .toString()
                                                                    .split(".")[1]?.length
                                                            }
                                                            enableScrollSpy={true}
                                                            duration={2}
                                                        />
                                                    </div>
                                                    <Text className='prose text-white mt-5'>
                                                        {parse(child.text ?? "")}
                                                    </Text>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }
                            return (
                                <div
                                    key={index}
                                    className={cn(
                                        "flex flex-col w-full even:bg-brand-light odd:bg-brand-dark",
                                        index % 2 == 0 && "lg:flex-col-reverse text-right"
                                    )}
                                >
                                    <div className='h-[350px] w-full overflow-hidden'>
                                        <Image
                                            src={child.image.url}
                                            height={child.image.height}
                                            width={child.image.width}
                                            alt={child.image.alt}
                                            className='size-full object-cover'
                                        />
                                    </div>
                                    <div className='p-8 flex flex-col justify-between h-full max-h-fit xl:max-h-[320px]'>
                                        <div className='text-h5 text-white leading-[120%]'>
                                            {child.headline}
                                        </div>
                                        <div>
                                            <div className='text-h0 xl:text-[70px] xxl:text-[90px] font-medium text-white leading-[120%]'>
                                                <CountUp
                                                    end={child.stat.value}
                                                    prefix={child.stat?.pre}
                                                    suffix={child.stat.post}
                                                    separator=''
                                                    decimal=','
                                                    decimals={
                                                        child.stat.value.toString().split(".")[1]
                                                            ?.length
                                                    }
                                                    enableScrollSpy={true}
                                                    duration={2}
                                                />
                                            </div>
                                            <Text className='prose text-white mt-5'>
                                                {parse(child.text ?? "")}
                                            </Text>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                </div>
            </div>
        </Section>
    );
}
